/**
 * The only Work affordance that still sits above the composer: picking a
 * stalled run back up. Live progress, browser hand-off, and approvals all
 * render inline in the transcript (WorkRunHeader / WorkActionCards), so the
 * composer area stays quiet while the agent is working.
 */
import React, { useCallback, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useCoworkerStore } from '@/store';
import { isWorkResumable, isWorkRepairPaused, isWorkScriptApprovalQuestion, workErrorForDisplay } from '@/constants/work';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import WorkCreditRecoveryCard from './WorkCreditRecoveryCard';
import { isWorkCreditError } from '@/lib/workCredits';
import reportWorkLaunchError from '@/lib/reportWorkLaunchError';

const HEADLINES = {
  failed: 'This work stopped early',
  stopped: 'You stopped this work',
  interrupted: 'This work is paused',
  budget_exceeded: 'This work reached its run limit',
};

// Continuing a capped-out run gives it a fresh budget, and it keeps everything
// it had already worked out — worth saying, since "budget" reads terminal.
const FALLBACK_REASON = 'Pick it back up from where it left off, or send a new direction below.';
const REASONS = {
  budget_exceeded: 'Continue summarizes the thread so far and keeps going with a fresh budget.',
};

export default function CoworkerLivePanel({ taskId, isActive, taskStatus, onResume, question }) {
  const { colors } = useTheme();
  const [isResuming, setIsResuming] = useState(false);
  const [resumeCreditError, setResumeCreditError] = useState(null);
  const isResumingRef = useRef(false);
  const taskError = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.error : null));
  const repairPaused = isWorkRepairPaused(taskError);

  const handleResume = useCallback(async () => {
    if (!onResume || isResumingRef.current) return;
    isResumingRef.current = true;
    setIsResuming(true);
    try {
      await onResume();
      setResumeCreditError(null);
    } catch (err) {
      if (isWorkCreditError(err)) {
        setResumeCreditError({ taskId, error: err });
        return;
      }
      reportWorkLaunchError(err, { source: 'work_resume', fallback: 'Failed to resume' });
    } finally {
      isResumingRef.current = false;
      setIsResuming(false);
    }
  }, [onResume, taskId]);

  if (!taskId || isActive || !isWorkResumable(taskStatus)) return null;

  const creditError = resumeCreditError?.taskId === taskId ? resumeCreditError.error : taskError;
  if (isWorkCreditError(creditError)) {
    return <WorkCreditRecoveryCard key={taskId} error={creditError} onResume={handleResume} isResuming={isResuming} />;
  }

  const reason = workErrorForDisplay(taskError);
  const holdForScript = isWorkScriptApprovalQuestion(question);

  return (
    <View style={[styles.box, repairPaused && styles.repairBox, { borderColor: colors.border_color_secondary }]}>
      <View style={[styles.copy, repairPaused && styles.repairCopy]}>
        <Text size="s" weight="semibold">{repairPaused ? 'This work is not finished yet' : (HEADLINES[taskStatus] || HEADLINES.interrupted)}</Text>
        {!!reason && <Text size="xxs" color="secondary" numberOfLines={repairPaused ? undefined : 2}>{reason}</Text>}
        <Text size="xxs" color="secondary" numberOfLines={repairPaused ? undefined : 2}>
          {holdForScript
            ? 'Read the numbered clip prompts on the card, then Approve or Revise. Resume would skip that review.'
            : repairPaused ? 'Review what’s been created, ask for changes in the message box below, or let the AI try fixing it again. Trying again uses credits.'
              : (REASONS[taskStatus] || FALLBACK_REASON)}
        </Text>
      </View>
      {holdForScript ? null : (
        <Button
          text={isResuming ? 'Resuming…' : repairPaused ? 'Try fixing it again' : (taskStatus === 'budget_exceeded' ? 'Continue' : 'Resume')}
          prependIcon="play"
          size="small"
          onPress={handleResume}
          isDisabled={isResuming}
          isLoading={isResuming}
          accessibilityRole="button"
          accessibilityLabel={repairPaused ? 'Try fixing it again using credits' : taskStatus === 'budget_exceeded' ? 'Continue work' : 'Resume work'}
        />
      )}
    </View>
  );
}

CoworkerLivePanel.propTypes = {
  taskId: PropTypes.string,
  isActive: PropTypes.bool,
  taskStatus: PropTypes.string,
  onResume: PropTypes.func,
  question: PropTypes.object,
};

const styles = StyleSheet.create({
  box: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  copy: {
    flex: 1,
    gap: 2,
  },
  repairBox: {
    flexWrap: 'wrap',
  },
  repairCopy: {
    flexBasis: 280,
  },
});
